import { useLayoutEffect, useRef } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';

am4core.useTheme(am4themes_animated);

const tradeProbData = [
    { date: new Date('2020-01-01'), office: 62, industrial: 74, retail: 48, residential: 55 },
    { date: new Date('2020-07-01'), office: 45, industrial: 70, retail: 32, residential: 50 },
    { date: new Date('2021-01-01'), office: 50, industrial: 76, retail: 38, residential: 58 },
    { date: new Date('2021-07-01'), office: 58, industrial: 80, retail: 44, residential: 62 },
    { date: new Date('2022-01-01'), office: 65, industrial: 85, retail: 50, residential: 66 },
    { date: new Date('2022-07-01'), office: 60, industrial: 82, retail: 47, residential: 63 },
    { date: new Date('2023-01-01'), office: 55, industrial: 79, retail: 43, residential: 60 },
    { date: new Date('2023-07-01'), office: 58, industrial: 81, retail: 46, residential: 62 },
    { date: new Date('2024-01-01'), office: 63, industrial: 84, retail: 52, residential: 65 },
    { date: new Date('2024-07-01'), office: 67, industrial: 87, retail: 55, residential: 68 },
];

const marketScoreData = [
    { market: 'Sydney', score: 74, outlook: 'Positive' },
    { market: 'Melbourne', score: 68, outlook: 'Positive' },
    { market: 'Singapore', score: 82, outlook: 'Strong' },
    { market: 'Tokyo', score: 71, outlook: 'Positive' },
    { market: 'Hong Kong', score: 52, outlook: 'Neutral' },
    { market: 'Seoul', score: 65, outlook: 'Positive' },
    { market: 'Shanghai', score: 48, outlook: 'Cautious' },
    { market: 'Mumbai', score: 60, outlook: 'Positive' },
];

const Predictability = () => {
    const probRef = useRef<any>(null);
    const scoreRef = useRef<any>(null);

    useLayoutEffect(() => {
        const chart = am4core.create('pred-prob-chart', am4charts.XYChart);
        probRef.current = chart;

        chart.data = tradeProbData;
        chart.legend = new am4charts.Legend();
        chart.legend.position = 'bottom';

        const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        dateAxis.dateFormats.setKey('month', 'MMM yyyy');
        dateAxis.periodChangeDateFormats.setKey('year', 'yyyy');

        const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.title.text = 'Trade Probability (%)';
        valueAxis.min = 20;
        valueAxis.max = 100;

        const configs = [
            { field: 'office', label: 'Office', color: '#1D4FA3' },
            { field: 'industrial', label: 'Industrial', color: '#2E8B57' },
            { field: 'retail', label: 'Retail', color: '#D6342F' },
            { field: 'residential', label: 'Residential', color: '#FF8C00' },
        ];

        configs.forEach(({ field, label, color }) => {
            const s = chart.series.push(new am4charts.LineSeries());
            s.dataFields.valueY = field;
            s.dataFields.dateX = 'date';
            s.name = label;
            s.strokeWidth = 2;
            s.stroke = am4core.color(color);
            s.tooltipText = `${label}: {valueY}%`;
            s.bullets.push(new am4charts.CircleBullet());
            s.tensionX = 0.8;
        });

        chart.cursor = new am4charts.XYCursor();

        return () => { chart.dispose(); };
    }, []);

    useLayoutEffect(() => {
        const chart = am4core.create('pred-score-chart', am4charts.XYChart);
        scoreRef.current = chart;

        chart.data = marketScoreData;

        const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = 'market';
        categoryAxis.title.text = 'Market';

        const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.title.text = 'Market Confidence Score';
        valueAxis.min = 0;
        valueAxis.max = 100;

        const series = chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.valueY = 'score';
        series.dataFields.categoryX = 'market';
        series.name = 'Confidence Score';
        series.columns.template.tooltipText = '{categoryX}: {valueY}/100';
        series.columns.template.width = am4core.percent(60);

        series.columns.template.adapter.add('fill', (fill, target: any) => {
            const val = target.dataItem?.valueY ?? 0;
            if (val >= 75) return am4core.color('#2E8B57');
            if (val >= 60) return am4core.color('#1D4FA3');
            if (val >= 50) return am4core.color('#FF8C00');
            return am4core.color('#D6342F');
        });
        series.columns.template.adapter.add('stroke', (stroke, target: any) => {
            const val = target.dataItem?.valueY ?? 0;
            if (val >= 75) return am4core.color('#2E8B57');
            if (val >= 60) return am4core.color('#1D4FA3');
            if (val >= 50) return am4core.color('#FF8C00');
            return am4core.color('#D6342F');
        });

        const valueLabel = series.bullets.push(new am4charts.LabelBullet());
        valueLabel.label.text = '{valueY}';
        valueLabel.label.dy = -10;
        valueLabel.label.fontSize = 12;

        chart.cursor = new am4charts.XYCursor();

        return () => { chart.dispose(); };
    }, []);

    return (
        <div style={{ padding: '24px 0' }}>
            <div style={{ padding: '0 30px', marginBottom: 32 }}>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1c4050', marginBottom: 8 }}>
                    Trade Predictability
                </h2>
                <p style={{ color: '#6e7189', fontSize: 14, maxWidth: 720 }}>
                    Probability of a commercial real estate transaction occurring by sector over time,
                    and market confidence scores across APAC gateway cities.
                </p>
            </div>

            <div style={{ padding: '0 30px', marginBottom: 40 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4, color: '#1c4050' }}>
                    Trade Probability by Sector (%) — 2020 to 2024
                </h3>
                <div id="pred-prob-chart" style={{ width: '100%', height: 380 }} />
                <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                    Source: Ascend Invest Predictive Model, MSCI Real Assets (Sample Data)
                </p>
            </div>

            <div style={{ padding: '0 30px' }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4, color: '#1c4050' }}>
                    Market Confidence Score by City (0–100)
                </h3>
                <div style={{ display: 'flex', gap: 24, marginBottom: 8, fontSize: 12, color: '#666' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 12, height: 12, background: '#2E8B57', display: 'inline-block', borderRadius: 2 }} /> Strong (75+)
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 12, height: 12, background: '#1D4FA3', display: 'inline-block', borderRadius: 2 }} /> Positive (60–74)
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 12, height: 12, background: '#FF8C00', display: 'inline-block', borderRadius: 2 }} /> Neutral (50–59)
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 12, height: 12, background: '#D6342F', display: 'inline-block', borderRadius: 2 }} /> Cautious (&lt;50)
                    </span>
                </div>
                <div id="pred-score-chart" style={{ width: '100%', height: 360 }} />
                <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                    Source: Ascend Invest Research, Oxford Economics (Sample Data)
                </p>
            </div>
        </div>
    );
};

export default Predictability;
